import { useNavigate } from 'react-router-dom'
import type { ReactElement } from 'react'

import { useT } from '../../lib/i18n'
import { Button } from '../../components/ui/Button'
import { IconButton } from '../../components/ui/IconButton'
import { Icon } from '../../components/ui/Icon'
import { useTour } from './store'
import { useTourRunner } from './useTourRunner'
import { TourSpotlight } from './TourSpotlight'

/**
 * The tour's dashboard: where you are, what to do, how to move on.
 *
 * Sits OUTSIDE the lit-up page and survives route changes, because a multi-page tour cannot have
 * its controls inside whatever is about to unmount. Mounted once, above the routed content —
 * `AppShell` is where it lives, since a tour only ever makes sense inside the signed-in app.
 */
export const TourPilot = (): ReactElement | null => {
  const navigate = useNavigate()
  useTourRunner()

  const { tour, index, waiting, rewound, target, next, previous, stop } = useTour()

  const t = useT()
  const step = tour?.steps[index]

  if (!tour || !step) return null

  const total = tour.steps.length

  // The click on the target can only happen if the target exists: the engine listens for clicks
  // that land on `step.target`, and a selector that hits nothing never fires. Such a step would
  // hang forever on "click the highlighted element" pointing at empty space, with "Skip" as the
  // only way out. While still waiting (`waiting`) the target may still arrive, and the button
  // should not be offered — it would be a shortcut past the one thing the step has to make happen.
  const clickable = step.advanceOnClick === true && (waiting || target !== null)
  const awaiting =
    !rewound &&
    (clickable ||
      step.advanceWhen !== undefined ||
      step.advanceWhenGone !== undefined ||
      step.advanceWhenFilled !== undefined)

  return (
    <>
      <TourSpotlight selector={target ? (step.target ?? null) : null} />

      <div className="fixed bottom-4 left-4 z-[100000] w-[calc(100%-2rem)] rounded-lg border border-hair bg-surface p-4 shadow-overlay sm:bottom-6 sm:left-6 sm:w-96">
        <div className="mb-2 flex items-center gap-2">
          <span className="t-small flex-1 text-ink-mute">
            {t('tours.pilot.progress', { title: t(tour.title), current: index + 1, total })}
          </span>
          <IconButton
            label={t('tours.pilot.close')}
            icon={<Icon name="close" size={14} />}
            size="sm"
            onClick={stop}
            tooltip={false}
          />
        </div>

        <div className="mb-3 h-1 overflow-hidden rounded-full bg-hair">
          <div
            className="h-full rounded-full bg-brass transition-[width] duration-200"
            style={{ width: `${String(Math.round((index / total) * 100))}%` }}
          />
        </div>

        <h3 className="t-h3 text-ink">{t(step.title)}</h3>
        <p className="t-small mb-3 text-ink-mute">{t(step.body)}</p>

        {waiting ? <p className="t-small text-ink-mute">{t('tours.pilot.opening')}</p> : null}

        {/* The step points at an element that is not there: say so, rather than leave an absent
            glow and someone who does not know what to look at. */}
        {!waiting && step.target && !target ? (
          <p className="t-small mb-2 text-warn-deep">{t('tours.pilot.targetMissing')}</p>
        ) : null}

        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="ghost"
            iconLeft={<Icon name="chevron-left" size={14} />}
            onClick={previous}
            disabled={index === 0}
          >
            {t('tours.pilot.back')}
          </Button>
          <span className="flex-1" />
          {awaiting ? (
            // The wait must never be a dead end: if the action never arrives — because the page
            // behaves differently than the tour expects — moving on must still be possible. A tour
            // that can only be escaped by closing it is worse than no tour.
            <div className="flex items-center gap-2">
              <span className="t-small text-ink-mute">
                {step.advanceWhenFilled ? t('tours.pilot.fillField') : t('tours.pilot.clickElement')}
              </span>
              <Button size="sm" variant="ghost" onClick={next}>
                {t('tours.pilot.skip')}
              </Button>
            </div>
          ) : (
            <Button
              size="sm"
              variant="primary"
              iconRight={<Icon name="chevron-right" size={14} />}
              onClick={() => {
                if (step.reload) {
                  window.location.assign(step.route ?? '/')
                  return
                }
                // The proposal is accepted with the button: the tour ends and only then the page
                // changes, not the other way — closing last would leave the panel lit on a screen
                // it no longer belongs to.
                if (step.finishAt) {
                  stop()
                  void navigate(step.finishAt)
                  return
                }
                next()
              }}
            >
              {step.reload
                ? t('tours.pilot.reload')
                : step.finishAt
                  ? t('tours.pilot.seeOtherTours')
                  : index + 1 === total
                    ? t('tours.pilot.done')
                    : t('tours.pilot.next')}
            </Button>
          )}
        </div>
      </div>
    </>
  )
}
